import { ChangeEvent, useEffect, useState } from 'react'

import { HiOutlineCheckCircle, HiOutlineLocationMarker } from 'react-icons/hi'
import usePlacesAutocomplete, {
  getGeocode,
  getLatLng,
} from 'use-places-autocomplete'

import {
  Input as ChakraInput,
  Icon,
  InputGroup,
  InputLeftElement,
  InputRightElement,
} from '@chakra-ui/react'
import styled from '@emotion/styled'
import {
  Combobox,
  ComboboxInput,
  ComboboxList,
  ComboboxOption,
  ComboboxPopover,
} from '@reach/combobox'
import '@reach/combobox/styles.css'

import { AddressSearchError } from './AddressSearchError'

const StyledInputGroup = styled(InputGroup)`
  .chakra-input {
    padding-left: 3rem;
    padding-right: ${(p) => (p['data-valid'] ? '3rem' : '1rem')};
    font-weight: 600;
    font-size: 16px;
    color: #000;

    ::placeholder {
      font-size: 14px;
      color: #2d2e41;
      opacity: 30%;
      font-style: italic;
      font-weight: 500;
    }
  }
`

interface AddressI {
  value: string
  latitude?: number
  longitude?: number
}

export interface AddressSearchInputIProps {
  onSelectAddress?: (address?: AddressI) => void
  defaultValue?: string
  isSubmitting?: boolean
  disabled?: boolean
}

export const AddressSearchInput = ({
  onSelectAddress,
  defaultValue,
  isSubmitting,
  disabled,
}: AddressSearchInputIProps) => {
  const {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    ready,
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    debounce: 300,
    defaultValue,
  })
  const [isDirty, setIsDirty] = useState(false)
  // If defaultValue exists, assume it's valid initially
  const [isError, setIsError] = useState(!defaultValue)

  useEffect(() => {
    setValue(defaultValue, false)
    if (defaultValue) {
      setIsError(false)
      setIsDirty(false)
    }
  }, [defaultValue])

  useEffect(() => {
    isSubmitting && handleOnBlur()
  }, [isSubmitting])

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    // Whenever input is changed, address is assumed to be incorrect
    setValue(e.target.value)
    onSelectAddress?.(undefined)
    setIsError(true)
  }

  const handleSelect = async (address: string) => {
    // Whenever address is selected from suggestions, it is assumed to be correct
    setValue(address, false)
    clearSuggestions()
    try {
      const results = await getGeocode({ address })
      const { lat, lng } = await getLatLng(results[0])
      onSelectAddress?.({ value: address, latitude: lat, longitude: lng })
      setIsError(false)
    } catch {
      setIsError(true)
    }
  }

  const handleOnBlur = () => {
    setIsDirty(true)
  }

  useEffect(() => {
    if (disabled && defaultValue === null) {
      setValue('')
    }
  }, [disabled])

  return (
    <Combobox onSelect={handleSelect}>
      <StyledInputGroup
        data-valid={!isError && !isDirty}
        size='lg'
        width='100%'
      >
        <InputLeftElement
          children={
            <Icon as={HiOutlineLocationMarker} boxSize={6} color='#4578D3' />
          }
        />
        <ComboboxInput
          as={ChakraInput}
          autoCapitalize='off'
          autoComplete='off'
          autoCorrect='off'
          bgColor='white'
          borderColor='#9FB8E5'
          borderWidth='2px'
          errorBorderColor='#D01030'
          isDisabled={!ready || disabled}
          isInvalid={isError && isDirty}
          onBlur={handleOnBlur}
          onChange={handleChange}
          placeholder='Start typing your address'
          spellCheck='false'
          value={value}
          variant='outline'
        />
        {!isError && !disabled && (
          <InputRightElement data-testid='input-valid-icon'>
            <Icon
              as={HiOutlineCheckCircle}
              bgColor='white'
              boxSize='42px'
              color='#2BD2B4'
              px='7px'
            />
          </InputRightElement>
        )}
      </StyledInputGroup>
      {status === 'OK' && (
        <ComboboxPopover portal={false}>
          <ComboboxList>
            {data.map(({ place_id, description }) => (
              <ComboboxOption key={place_id} value={description} />
            ))}
          </ComboboxList>
        </ComboboxPopover>
      )}
      {isError && isDirty && <AddressSearchError />}
    </Combobox>
  )
}
